import {
  fetchAuthSession,
  getCurrentUser,
  signInWithRedirect,
  signOut,
} from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import {
  useMutation,
  useQuery,
  useQueryClient,
  queryOptions,
} from "@tanstack/react-query";
import {
  OpenAPI,
  User,
  DefaultService as UsersService,
} from "../api/SolarCloudApi";
import { logger } from "./logger";
import {
  Environment,
  useEnvironmentContext,
} from "@/contexts/EnvironmentContext";

type AuthenticationState = {
  initialised: boolean;
  environment: Environment;
};

const authState: AuthenticationState = {
  initialised: false,
  environment: {} as Environment,
};

const sanitizeToken = (token?: string): string | undefined => {
  if (!token) return token;
  return token
    .replace(/^["']+|["']+$/g, "")
    .replace(/[\x00-\x1F\x7F]+/g, "")
    .trim();
};

const authInit = (environment: Environment) => {
  if (!environment || authState.initialised) {
    return;
  }

  authState.initialised = true;
  authState.environment = environment;
};

type AuthenticationLoggedIn = {
  loggedIn: true;
  user: User;
  email: string;
  idToken: string;
  accessToken: string;
  federated: boolean;
};

type AuthenticationLoggedOut = {
  loggedIn: false;
  user: undefined;
  googleSignIn: (redirectUrl?: string) => Promise<void>;
  appleSignIn: (redirectUrl?: string) => Promise<void>;
  facebookSignIn: (redirectUrl?: string) => Promise<void>;
};

export type Authentication = AuthenticationLoggedIn | AuthenticationLoggedOut;

export function getAuthenticationQueryOptions(
  environment: Environment,
  oauthState?: string
) {
  return queryOptions({
    queryKey: ["authentication"],
    queryFn: async (): Promise<Authentication> => {
      const authenticationLoggedOut = (): AuthenticationLoggedOut => {
        return {
          loggedIn: false,
          user: undefined,
          googleSignIn: async (redirectUrl?: string) => {
            await signInWithRedirect({
              provider: "Google",
              customState: `${oauthState ?? ""}${redirectUrl ?? ""}`,
            });
          },
          appleSignIn: async (redirectUrl?: string) => {
            await signInWithRedirect({
              provider: { custom: "SignInWithApple" },
              customState: `${oauthState ?? ""}${redirectUrl ?? ""}`,
            });
          },
          facebookSignIn: async (redirectUrl?: string) => {
            await signInWithRedirect({
              provider: "Facebook",
              customState: `${oauthState ?? ""}${redirectUrl ?? ""}`,
            });
          },
        };
      };

      if (!environment) return authenticationLoggedOut();

      try {
        const user = await getCurrentUser();
        const session = await fetchAuthSession();

        const idToken = sanitizeToken(session.tokens?.idToken?.toString());
        const accessToken = sanitizeToken(session.tokens?.accessToken?.toString());

        if (!idToken || !accessToken) return authenticationLoggedOut();

        OpenAPI.TOKEN = idToken;
        const profileUser = await UsersService.getProfile();

        const decoded = JSON.parse(atob(idToken.split(".")[1])) as Record<
          string,
          any
        >;

        return {
          loggedIn: true,
          user: profileUser,
          email: decoded.email,
          idToken,
          accessToken,
          federated: Array.isArray(decoded.identities),
        };
      } catch (error: unknown) {
        return authenticationLoggedOut();
      }
    },
    staleTime: 15 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });
}

export const useAuthenticationQuery = (oauthState?: string) => {
  const queryClient = useQueryClient();
  const environment = useEnvironmentContext();
  authInit(environment);

  // Set up Hub listener
  Hub.listen("auth", (hubPayload) => {
    const { event } = hubPayload.payload;
    const data = (hubPayload.payload as { data?: unknown }).data;

    switch (event) {
      case "signedIn":
      case "signedOut":
      case "tokenRefresh":
        queryClient.invalidateQueries({ queryKey: ["authentication"] });
        break;
      case "signInWithRedirect_failure":
        console.error("An error has occurred during the OAuth flow.", data);
        logger.error("Sign in failure", data);
        break;
      // case "customOAuthState":
      //   console.log("Custom Auth State", data);
      //   break;
      case "signInWithRedirect":
        console.log("OAuth success:", data);
        break;
      default:
        break;
    }
  });

  const query = useQuery(
    getAuthenticationQueryOptions(environment, oauthState)
  );
  return query;
};

export const useAuthenticationMutation = () => {
  const { data: authentication } = useAuthenticationQuery();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async () => {
      if (authentication?.loggedIn) {
        await signOut();
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["authentication"] });
    },
    onError: (error) => {
      console.error("Failed to sign out", error);
    },
  });

  return mutation;
};

export const getIdToken = async (): Promise<string | undefined> => {
  if (!authState.initialised) return undefined;

  try {
    const session = await fetchAuthSession();
    return sanitizeToken(session.tokens?.idToken?.toString());
  } catch {
    return undefined;
  }
};

export const getAccessToken = async (): Promise<string | undefined> => {
  if (!authState.initialised) return undefined;

  try {
    const session = await fetchAuthSession();
    return sanitizeToken(session.tokens?.accessToken?.toString());
  } catch {
    return undefined;
  }
};
